import './index.css'
import { useState, useEffect } from 'react'
import { Check, X, CreditCard, ArrowDownRight, ArrowUpRight, Target } from 'lucide-react'
import type { Transaction, Card, Goal } from '../../types'

interface SetupChecklistProps {
  transactions: Transaction[]
  cards: Card[]
  goals: Goal[]
  onOpenAddTransaction: () => void
  onNavigateToCards: () => void
  onNavigateToGoals: () => void
}

export default function SetupChecklist({ transactions, cards, goals, onOpenAddTransaction, onNavigateToCards, onNavigateToGoals }: SetupChecklistProps) {
  const [dismissed, setDismissed] = useState(true) // default to true to prevent flash, then check storage

  useEffect(() => {
    const isDismissed = localStorage.getItem('bc_setup_checklist_dismissed') === 'true'
    setDismissed(isDismissed)
  }, [])

  const hasCard = cards.length > 0
  const hasIncome = transactions.some(t => t.kind === 'in')
  const hasExpense = transactions.some(t => t.kind === 'out')
  const hasGoal = goals && goals.length > 0

  const tasks = [
    { id: 'card', label: 'Cadastrar um cartão de crédito', done: hasCard, icon: <CreditCard size={14} />, action: onNavigateToCards },
    { id: 'income', label: 'Registrar uma receita', done: hasIncome, icon: <ArrowDownRight size={14} />, action: onOpenAddTransaction },
    { id: 'expense', label: 'Registrar uma despesa', done: hasExpense, icon: <ArrowUpRight size={14} />, action: onOpenAddTransaction },
    { id: 'goal', label: 'Criar uma meta', done: hasGoal, icon: <Target size={14} />, action: onNavigateToGoals },
  ]

  const completedCount = tasks.filter(t => t.done).length
  const progress = Math.round((completedCount / tasks.length) * 100)

  if (dismissed || completedCount === tasks.length) return null

  function handleDismiss() {
    localStorage.setItem('bc_setup_checklist_dismissed', 'true')
    setDismissed(true)
  }

  return (
    <div className="bc-card bc-setup-checklist">
      <div className="bc-setup-checklist__header">
        <h3 className="bc-setup-checklist__title">Bem-vindo ao BagCash! 🎉</h3>
        <button className="bc-iconbtn" onClick={handleDismiss} title="Ocultar checklist">
          <X size={18} />
        </button>
      </div>

      <p style={{ fontSize: '0.875rem', color: 'var(--ink-500)', margin: '0 0 12px' }}>
        Complete os passos abaixo para configurar seu controle financeiro.
      </p>

      <div className="bc-setup-checklist__progress-container">
        <div className="bc-setup-checklist__progress-bar-wrap">
          <div className="bc-setup-checklist__progress-bar" style={{ width: `${progress}%` }} />
        </div>
        <span className="bc-setup-checklist__progress-text">{progress}%</span>
      </div>

      <ul className="bc-setup-checklist__list">
        {tasks.map(task => (
          <li key={task.id} className={'bc-setup-checklist__item' + (task.done ? ' is-done' : '')}>
            <div className={'bc-setup-checklist__icon' + (task.done ? ' is-done' : '')}>
              {task.done ? <Check size={14} /> : task.icon}
            </div>
            <span className="bc-setup-checklist__text">{task.label}</span>
            {!task.done && (
              <button className="bc-btn bc-btn--ghost bc-btn--sm bc-setup-checklist__action" onClick={task.action}>
                Fazer agora
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}
